import { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { skipToken } from '@reduxjs/toolkit/query';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import useSelector from 'shared/hooks/useSelector';

import { userSpaceRenovationRequestsApi } from '../../../../api';

import { PHONE_PATTERN, ValidationWarningMessage } from '../../constants';
import type { FormState } from '../../SpaceRenovationRequestCreateForm.type';
import type { ExecutorProps } from '../RenovationTypes.type';

import Stack from '@mui/material/Stack';
import DatePicker from 'components/controls/date-picker';
import ValidationWarning from 'components/validation-warning';
import ControlLabel from 'shared/components/ControlLabel';
import Input from 'shared/components/Input';
import { RenovationExecutorFieldContainer } from '../RenovationTypes.style';

const currentYear = new Date().getFullYear();

const RenovationExecutorPortalUser = ({ idx }: ExecutorProps) => {
  const coopId = useCooperativeId();
  const res = useSelector((state) =>
    userSpaceRenovationRequestsApi.endpoints.getUserSpaceRenovationRequestCreateFormOptions.select(
      coopId ?? skipToken
    )(state)
  );

  const {
    clearErrors,
    control,
    formState: { errors },
    register,
  } = useFormContext<FormState>();

  const portalUserInfo = res.data?.PortalUserInfo ?? null;

  const disabledFirstName = !!portalUserInfo?.FirstName;
  const disabledLastName = !!portalUserInfo?.LastName;
  const disabledBirthday = !!portalUserInfo?.Birthday;
  const disabledEmail = !!portalUserInfo?.Email?.Email;
  const disabledPhone = !!portalUserInfo?.Phone?.Phone;

  const baseFieldName = useMemo(() => `renovationTypes.${idx}` as const, [idx]);

  return (
    <Stack spacing={2} sx={{ width: { xs: '100%', md: '80%' } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required={!disabledFirstName}>Etunimi</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.portalUserFirstName?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseFieldName}.portalUserFirstName`, {
                  required: disabledFirstName
                    ? false
                    : ValidationWarningMessage.Required,
                })}
                disabled={disabledFirstName}
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>

        <RenovationExecutorFieldContainer>
          <ControlLabel required={!disabledLastName}>Sukunimi</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.portalUserLastName?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(`${baseFieldName}.portalUserLastName`, {
                  required: disabledLastName
                    ? false
                    : ValidationWarningMessage.Required,
                })}
                disabled={disabledLastName}
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required={!disabledBirthday}>Syntymäaika</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.portalUserBirthday?.message}
            handleInitErrors={clearErrors}
            Component={
              <Controller
                control={control}
                name={`${baseFieldName}.portalUserBirthday`}
                rules={{
                  required: disabledBirthday
                    ? false
                    : ValidationWarningMessage.Required,
                }}
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    date={value ?? null}
                    disabled={disabledBirthday}
                    endIn={currentYear}
                    handleChange={onChange}
                    placeholder="DD.M.YYYY"
                    variant="underline"
                  />
                )}
              />
            }
          />
        </RenovationExecutorFieldContainer>

        <RenovationExecutorFieldContainer>
          <ControlLabel required={!disabledEmail}>
            Sähköpostiosoite
          </ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.portalUserEmail?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(
                  `${baseFieldName}.portalUserEmail`,
                  disabledEmail
                    ? undefined
                    : {
                        required: ValidationWarningMessage.Required,
                      }
                )}
                disabled={disabledEmail}
                placeholder="mail@mail.com"
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <RenovationExecutorFieldContainer>
          <ControlLabel required={!disabledPhone}>
            Matkapuhelinnumero
          </ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.portalUserPhone?.message}
            handleInitErrors={clearErrors}
            Component={
              <Input
                {...register(
                  `${baseFieldName}.portalUserPhone`,
                  disabledPhone
                    ? undefined
                    : {
                        required: ValidationWarningMessage.Required,
                        pattern: {
                          value: PHONE_PATTERN,
                          message: ValidationWarningMessage.Invalid,
                        },
                      }
                )}
                disabled={disabledPhone}
                placeholder="+1234567890"
                variant="underline"
              />
            }
          />
        </RenovationExecutorFieldContainer>
      </Stack>
    </Stack>
  );
};

export default RenovationExecutorPortalUser;
